import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiService } from 'services/ApiService';
import type {
  AppointmentRule,
  CreateAppointmentRuleRequest,
  UpdateAppointmentRuleRequest,
} from 'models/appointments';
import { NOT_FOUND_MESSAGE } from 'utils/apiError';

/** Public shape returned by {@link useAppointmentRules}. */
interface UseAppointmentRulesResult {
  rules: AppointmentRule[];
  isLoading: boolean;
  error: string | null;
  /**
   * (Re)fetch the rule list. `existingError` is preserved into the error
   * state after the refresh so a failed mutation's 404/422 reason is never
   * masked by the re-sync.
   */
  load: (existingError?: string | null) => Promise<void>;
  create: (payload: CreateAppointmentRuleRequest) => Promise<boolean>;
  update: (ruleId: string, payload: UpdateAppointmentRuleRequest) => Promise<boolean>;
  toggle: (rule: AppointmentRule) => Promise<boolean>;
  remove: (ruleId: string) => Promise<boolean>;
}

const DEFAULT_LOAD_ERROR = 'Failed to load appointment rules.';
const DEFAULT_MUTATION_ERROR = 'The appointment rule operation failed.';

/**
 * Appointment-rule CRUD state for a workflow template. First hook in the repo —
 * sets the layering precedent between components and ApiService.
 *
 * - No fetch while `templateId` is undefined (create-mode gate).
 * - The server is the source of truth: every mutation re-syncs via `load`
 *   on completion; responses are never merged into the local list.
 * - A failed mutation's message is passed to `load` as `existingError` so it
 *   survives the refresh instead of being cleared.
 * - A 404 on load means "no rules yet": the list goes empty, no error.
 * - Every `load` carries a cancel token; a superseded or post-unmount load
 *   never touches state (mounted guard).
 */
export default function useAppointmentRules(
  templateId: string | undefined,
): UseAppointmentRulesResult {
  const [rules, setRules] = useState<AppointmentRule[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(templateId));
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef<boolean>(true);
  const loadTokenRef = useRef<number>(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadTokenRef.current += 1; // invalidate any in-flight load
    };
  }, []);

  const load = useCallback(
    async (existingError: string | null = null): Promise<void> => {
      if (!templateId) return;
      const token = (loadTokenRef.current += 1);
      const isCurrent = (): boolean =>
        mountedRef.current && loadTokenRef.current === token;

      setIsLoading(true);
      try {
        const fetched = await ApiService.listAppointmentRules(templateId);
        if (!isCurrent()) return;
        setRules(fetched);
        setError(existingError);
      } catch (e) {
        if (!isCurrent()) return;
        if (e instanceof Error && e.message === NOT_FOUND_MESSAGE) {
          setRules([]);
          setError(existingError);
        } else {
          setError(
            existingError ?? (e instanceof Error ? e.message : DEFAULT_LOAD_ERROR),
          );
        }
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    },
    [templateId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const runMutation = useCallback(
    async (operation: () => Promise<unknown>): Promise<boolean> => {
      let mutationError: string | null = null;
      try {
        await operation();
      } catch (e) {
        mutationError = e instanceof Error ? e.message : DEFAULT_MUTATION_ERROR;
      }
      await load(mutationError);
      return mutationError === null;
    },
    [load],
  );

  const create = useCallback(
    async (payload: CreateAppointmentRuleRequest): Promise<boolean> => {
      if (!templateId) return false;
      const id = templateId;
      return runMutation(() => ApiService.createAppointmentRule(id, payload));
    },
    [templateId, runMutation],
  );

  const update = useCallback(
    async (ruleId: string, payload: UpdateAppointmentRuleRequest): Promise<boolean> => {
      if (!templateId) return false;
      const id = templateId;
      return runMutation(() => ApiService.updateAppointmentRule(id, ruleId, payload));
    },
    [templateId, runMutation],
  );

  const toggle = useCallback(
    async (rule: AppointmentRule): Promise<boolean> => {
      if (!templateId) return false;
      if (rule.minimumNoticeHours === null) {
        setError('Cannot update a rule without a valid minimum notice period.');
        return false;
      }
      const id = templateId;
      const minimumNoticeHours = rule.minimumNoticeHours;
      return runMutation(() =>
        ApiService.updateAppointmentRule(id, rule.id, {
          ruleType: rule.ruleType,
          minimumNoticeHours,
          isActive: !rule.isActive,
        }),
      );
    },
    [templateId, runMutation],
  );

  const remove = useCallback(
    async (ruleId: string): Promise<boolean> => {
      if (!templateId) return false;
      const id = templateId;
      return runMutation(() => ApiService.deleteAppointmentRule(id, ruleId));
    },
    [templateId, runMutation],
  );

  return { rules, isLoading, error, load, create, update, toggle, remove };
}
